import {AfterViewInit, Component, ElementRef, input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DjangoApiService} from "../../../../shared/services/django-api.service";
import {CardsBasicComponent} from "../../../../shared/components/cards/cards-basic/cards-basic.component";
import {SectionDetailAdminComponent} from "../section-detail-admin/section-detail-admin.component";
import {MatButtonModule} from "@angular/material/button";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {MatIconModule} from "@angular/material/icon";
import {MatTabsModule} from "@angular/material/tabs";
import {MatTooltipModule} from "@angular/material/tooltip";
import {SnackbarService} from "../../../../shared/services/snackbar.service";
import Sortable from 'sortablejs';
import {ResizableModule, ResizeEvent} from 'angular-resizable-element';
import {Subscription} from "rxjs";
import {LanguageService} from "../../../../shared/services/language.service";
import {ContentBlockEditComponent} from "../content-block-edit/content-block-edit.component";
import {ActivatedRoute} from "@angular/router";
import {BlockStyleEditorComponent} from "../../../block-style-editor/block-style-editor/block-style-editor.component";
import {DialogConfirmComponent} from "../../../../shared/components/dialogs/dialog-confirm/dialog-confirm.component";
import {MatDialog} from "@angular/material/dialog";


@Component({
  selector: 'app-section-list-admin',
  standalone: true,
  imports: [
    CardsBasicComponent,
    SectionDetailAdminComponent,
    MatButtonModule,
    TranslateModule,
    MatIconModule,
    MatTabsModule,
    MatTooltipModule,
    ResizableModule,
    ContentBlockEditComponent,
    BlockStyleEditorComponent
  ],
  templateUrl: './section-list-admin.component.html',
  styleUrls: ['./section-list-admin.component.css']
})
export class SectionListAdminComponent implements OnInit, AfterViewInit, OnDestroy {
  productID = input<any>();
  productId!: number;
  pageId!: number;
  sections: any[] = [];
  newSection: any = {};
  isAddingNewSection: boolean = false;
  editSectionId: number | null = null;

  editLanguage!: string;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;
  private subscriptions: Subscription = new Subscription();

  @ViewChild('sortableContainer', {static: false}) sortableContainer!: ElementRef;

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    private _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute,
    private _dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.setLanguageSubscription();
    this.getRouteParams()
    this.loadSectionList();
    this.createEmptySection();
    this.setLanguageSubscription()
  }

  getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = this._route.snapshot.parent?.params['productId'];
        this.pageId = this._route.snapshot.parent?.params['pageId'];
      })
    );
  }

  private setLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.editLanguage = this.currentLanguage;
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange
        .subscribe((event: LangChangeEvent) => {
          this.currentLanguage = event.lang;
        })
    );
  }

  private createEmptySection() {
    const lastOrder = this.sections.length > 0 ? Math.max(...this.sections.map(section => section.order)) : 0;
    const translations: { [key: string]: { title: string} } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: ''
      };
    });

    this.newSection = {
      product: this.productId,
      page: this.pageId,
      parent: null,
      width: 3,
      order: lastOrder,
      translations: translations
    };
  }

  loadSectionList() {
    this.subscriptions.add(
      this._apiService.fetchSectionList(this.pageId).subscribe({
        next: (response: any) => {
          this.sections = response;
          console.log('Section List Component', this.sections);
        },
        error: (error: any) => {
          console.log(error);
        }
      })
    );
  }

  addSection() {
    this.subscriptions.add(
      this._apiService.createSection(this.newSection).subscribe({
        next: () => {
          this.loadSectionList();
          this.toggleFormVisibility();
          this._snackbarService.show('New order saved');
        },
        error: (error: any) => {
          console.log(error);
        }
      })
    );
  }

  saveOrder() {
    this.subscriptions.add(
      this._apiService.updateSectionOrder(this.sections).subscribe({
        next: () => {
          this._snackbarService.show('New order saved');
        },
        error: (error: any) => {
          console.log(error);
        }
      })
    );
  }


  updateSectionOrder() {
    const orderedIds = Array.from(this.sortableContainer.nativeElement.children)
      .map((child) => (child as HTMLElement).getAttribute('data-id'))
      .filter(id => id !== null); // Ensure no null values

    if (orderedIds.length !== this.sections.length) {
      console.error('Mismatch between orderedIds and sections length');
      return;
    }

    this.sections.sort((a, b) => orderedIds.indexOf(a.id.toString()) - orderedIds.indexOf(b.id.toString()));

    this.sections.forEach((section, index) => {
      section.order = index;
    });

    this.saveOrder();
  }


  confirmDelete(id: number, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    DialogConfirmComponent.openDeleteDialog(this._dialog, this._translate).subscribe(result => {
      if (result) {
        this.deleteSection(id);
      }
    });
  }

  deleteSection(id: number) {
    this.subscriptions.add(
      this._apiService.deleteSection(id).subscribe({
        next: () => {
          this._snackbarService.show('Item successfully deleted');
          this.loadSectionList();
        },
        error: (error: any) => {
          console.log(error);
        }
      })
    );
  }

  onResizeEnd(event: ResizeEvent, section: any): void {
    if (event.edges.right != undefined) {
      let edges = Number(event.edges.right);
      let windowRatio = window.innerWidth * 0.2;
      let containerWidth = this.sortableContainer.nativeElement.clientWidth;
      let containerRatio = containerWidth * 0.25;

      edges > 0 ? this.handlePositiveEdges(edges, containerRatio, section) : this.handleNegativeEdges(edges, windowRatio, section);
      this.saveOrder();

    } else {
      console.error('Resize event does not contain rectangle dimensions');
    }
  }

  handlePositiveEdges(edges: number, containerRatio: number, section: any) {
    if (edges > containerRatio) {
      if (edges > containerRatio * 2 && section.width === 1) {
        section.width = 3;
      } else if (section.width === 2 || section.width === 1) {
        section.width++;
      }
    }
  }

  handleNegativeEdges(edges: number, containerRatio: number, section: any) {
    let absoluteEdges = edges * -1;

    if (absoluteEdges > containerRatio) {
      if (absoluteEdges > containerRatio * 2 && section.width === 3) {
        section.width = 1;
      } else if (section.width === 2) {
        section.width = 1;
      } else if (section.width === 3) {
        section.width = 2;
      }
    }
  }


  toggleFormVisibility(): void {
    this.createEmptySection();
    this.isAddingNewSection = !this.isAddingNewSection;
  }

  toggleEditSection(id: number, event?: Event) {
    event?.stopPropagation();
    this.editSectionId = (this.editSectionId == id) ? null : id;
    if (this.editSectionId == null) {
      this.loadSectionList();
    }
  }

  updateSectionTitle(section: any) {
    this.subscriptions.add(
      this._apiService.updateSection(section).subscribe({
        next: () => {
          this._snackbarService.show('Item successfully updated');
          this.loadSectionList();
          this.editSectionId = null;
        },
        error: (error: any) => {
          console.log(error);
        }
      })
    );
  }

  ngAfterViewInit() {
    new Sortable(this.sortableContainer.nativeElement, {
      animation: 300,
      handle: '.drag-indicator',
      onEnd: (event) => {
        this.updateSectionOrder();
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }


}
